import { kUserFilesFileNameMaxLength, kUserFilesFolderNameMaxLength } from '@/user_files/limits'
import { splitFilename } from '@/user_files/utils/filename'

/**
 * Returns a unique name that does not conflict with any name in `existingNames`.
 * Appends " (N)" before the extension (files only), incrementing N until a free name is found.
 * Truncates the base name if needed to stay within the DB length limit.
 *
 * Examples:
 *   "report.pdf" + {"report.pdf"} → "report (1).pdf"
 *   "report.pdf" + {"report.pdf", "report (1).pdf"} → "report (2).pdf"
 *   "notes"      + {"notes"}      → "notes (1)"
 *   "v1.0" (folder) + {"v1.0"}   → "v1.0 (1)"
 */
export function resolveUploadName(filename: string, existingNames: ReadonlySet<string>, type: 'file' | 'folder' = 'file'): string {
  if (!existingNames.has(filename)) return filename

  const maxLength = type === 'folder' ? kUserFilesFolderNameMaxLength : kUserFilesFileNameMaxLength
  const { base: rawBase, ext } = type === 'file' ? splitFilename(filename) : { base: filename, ext: '' }

  // Strip an existing " (N)" suffix so we increment cleanly: "file (2)" → "file (3)", not "file (2) (1)"
  const existingSuffix = rawBase.match(/ \((\d+)\)$/)
  const base = existingSuffix ? rawBase.slice(0, -existingSuffix[0].length) : rawBase
  let counter = existingSuffix ? parseInt(existingSuffix[1], 10) + 1 : 1

  while (true) {
    const suffix = ` (${counter})`
    const truncatedBase = base.slice(0, Math.max(0, maxLength - ext.length - suffix.length))
    const candidate = `${truncatedBase}${suffix}${ext}`
    if (!existingNames.has(candidate)) return candidate
    counter++
  }
}
